import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { activationAPI } from '../api/activation';
import { useAuthStore } from '../store/auth';
import { TopBar } from '../components/ui/TopBar';
import { Card } from '../components/ui/Card';
import { FormRow } from '../components/ui/FormRow';
import { Button } from '../components/ui/Button';

export function Activation() {
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile) as Record<string, any> | null;
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const balance = profile?.activation_code ?? profile?.balances?.activation_code ?? '0';

  const redeem = useMutation({
    mutationFn: async () => {
      const res = await activationAPI.redeem({ amount } as unknown as Parameters<typeof activationAPI.redeem>[0]);
      return res as any;
    },
    onSuccess: (data: any) => {
      setMsg({ type: 'ok', text: `${t('common.redeemSuccess', '成功兑换')} ${data?.data?.amount ?? amount} ${t('common.activationCode', '激活码')}` });
      setAmount('');
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (e: any) => {
      setMsg({ type: 'err', text: e?.message ?? t('common.redeemFailed', '兑换失败') });
    },
  });

  const handleRedeem = () => {
    setMsg(null);
    if (!amount || Number(amount) <= 0) {
      setMsg({ type: 'err', text: t('common.enterPositiveAmount', '请输入大于 0 的数量') });
      return;
    }
    redeem.mutate();
  };

  return (
    <div className="min-h-screen bg-surface-base pb-20">
      <div className="absolute inset-0 -z-10 bg-space opacity-70" />
      <TopBar title={t('common.activationCode', '激活码')} />

      {/* Balance hero */}
      <div className="px-4 pt-4">
        <div className="relative overflow-hidden rounded-3xl border border-brand-500/25 bg-gradient-to-br from-surface-elevated to-surface-base p-6 shadow-glow-brand">
          <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-brand-500/15 blur-3xl" />
          <div className="relative text-center">
            <div className="text-xs text-text-muted mb-1 uppercase tracking-widest">
              {t('common.activationBalance', '激活码余额')}
            </div>
            <div className="text-4xl font-bold text-text-primary font-mono">
              {Number(balance).toFixed(2)}
            </div>
            <div className="mt-1 text-xs text-brand-300">AC</div>
          </div>
        </div>
      </div>

      {/* Redeem form */}
      <div className="mx-4 mt-4">
        <Card className="p-5">
          <div className="mb-4">
            <h2 className="text-sm font-semibold text-text-primary mb-1">
              {t('common.redeemActivation', '兑换激活码')}
            </h2>
            <p className="text-xs text-text-muted">
              {t('common.redeemActivationHint', '消耗激活码余额激活您的账户')}
            </p>
          </div>

          <div className="space-y-4">
            <FormRow label={t('common.redeemAmount', '兑换数量')} required>
              <input
                type="number"
                step="any"
                min="0"
                placeholder={t('common.redeemAmount', '兑换数量')}
                className="input-dark w-full py-3"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </FormRow>

            <Button
              type="button"
              onClick={handleRedeem}
              isLoading={redeem.isPending}
              fullWidth
              size="lg"
            >
              {t('common.redeemNow', '立即兑换')}
            </Button>
          </div>
        </Card>
      </div>

      {/* Status message */}
      {msg && (
        <div className={`mx-4 mt-3 rounded-xl border px-4 py-3 text-sm ${
          msg.type === 'ok'
            ? 'border-positive/30 bg-positive/10 text-positive'
            : 'border-negative/30 bg-negative/10 text-negative'
        }`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}
